import {
  startOfISOWeek,
  endOfISOWeek,
  addWeeks,
  subWeeks,
  format,
  getISOWeek,
  getISOWeekYear,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInYears,
} from 'date-fns';

/**
 * Get current ISO week string (e.g., "2024-W48")
 */
export function getCurrentWeekString(): string {
  const now = new Date();
  const year = getISOWeekYear(now);
  const week = getISOWeek(now);
  return `${year}-W${week.toString().padStart(2, '0')}`;
}

/**
 * Format a date as ISO week string (e.g., "2024-W48")
 */
export function formatWeekString(date: Date): string {
  const year = getISOWeekYear(date);
  const week = getISOWeek(date);
  return `${year}-W${week.toString().padStart(2, '0')}`;
}

/**
 * Parse ISO week string to Date (start of that week)
 */
export function parseWeekString(weekString: string): Date {
  const match = weekString.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid week string: ${weekString}`);
  }
  const year = parseInt(match[1]);
  const week = parseInt(match[2]);

  // January 4th is always in week 1
  const jan4 = new Date(year, 0, 4);
  const startOfYear = startOfISOWeek(jan4);
  return addWeeks(startOfYear, week - 1);
}

/**
 * Get ISO date strings for week start and end (for API queries)
 */
export function getWeekBounds(weekString: string): {
  start: string;
  end: string;
} {
  const weekStart = parseWeekString(weekString);
  const weekEnd = addWeeks(weekStart, 1);
  return {
    start: format(weekStart, 'yyyy-MM-dd'),
    end: format(weekEnd, 'yyyy-MM-dd'),
  };
}

/**
 * Get previous week string
 */
export function getPreviousWeek(weekString: string): string {
  const date = parseWeekString(weekString);
  return formatWeekString(subWeeks(date, 1));
}

/**
 * Get next week string
 */
export function getNextWeek(weekString: string): string {
  const date = parseWeekString(weekString);
  return formatWeekString(addWeeks(date, 1));
}

/**
 * Get human-readable week title (e.g., "Week 48, 2024")
 */
export function getWeekTitle(weekString: string): string {
  const date = parseWeekString(weekString);
  return `Week ${getISOWeek(date)}, ${getISOWeekYear(date)}`;
}

/**
 * Get date range string for week (e.g., "25 Nov - 1 Dec")
 */
export function getWeekDateRange(weekString: string): string {
  const weekStart = parseWeekString(weekString);
  const weekEnd = endOfISOWeek(weekStart);
  return `${format(weekStart, 'd MMM')} - ${format(weekEnd, 'd MMM')}`;
}

/**
 * Formats a Date object into a human-readable date string.
 * @param date - The date to format
 * @returns Formatted date string (e.g., "15 Dec 2023")
 */
export function formatDate(date: Date): string;
export function formatDate(date: Date | null): string | null;
export function formatDate(date: Date | null): string | null {
  if (!date) return null;
  return format(date, 'dd MMM yyyy');
}

/**
 * Format time from date (e.g., "09:30")
 */
export function formatTime(date: Date): string {
  return format(date, 'HH:mm');
}

/**
 * Formats a decimal hour value into a human-readable string.
 * @param hours - The number of hours as a decimal (e.g., 1.5 for 1h 30m)
 * @returns Formatted string (e.g., "1h 30m" or "2h")
 */
export function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Format time distance compactly: "5 minutes ago" -> "-5m"
 * @param date - The date to format
 * @param now - Reference time (pass this to avoid recalculating in loops)
 * @returns Compact string like "now", "-5m", "-2h", "-3d", "-1y"
 */
export function formatTimeCompact(date: Date, now: Date = new Date()): string {
  const mins = differenceInMinutes(now, date);
  if (mins < 1) return 'now';
  if (mins < 60) return `-${mins}m`;

  const hours = differenceInHours(now, date);
  if (hours < 24) return `-${hours}h`;

  const days = differenceInDays(now, date);
  const years = differenceInYears(now, date);
  if (years < 1) return `-${days}d`;

  return `-${years}y`;
}
